/**
 * CRM global search: which result types a viewer may see (2026-10-02).
 *
 * The search box at the top of the CRM sidebar finds customers, prospects,
 * contacts, invoices, quotes, files and resellers. A viewer must only ever get
 * the types whose PAGE they can open. A hit links to that page, so a hit for a
 * page you cannot open is data you were not meant to have.
 *
 * This is the ONE map from result type to CRM SPA page-access string. Two sides
 * read it:
 *   - the dashboard server, which mints `searchTypesForPages(<the viewer's CRM
 *     page set>)` as the `crm_search_types` claim on every CRM token. crm-api
 *     intersects the claim with its own page checks, so the claim can only
 *     narrow a search (xop-brain SearchController#staff_allowed_search_types);
 *   - the CRM SPA, which filters the response through the same function. That
 *     is display only; the server is the guard.
 *
 * Invoices and quotes share `invoices:access`, which the dashboard emits only
 * with `crm:financials`, so the financial wall closes both. Resellers ride
 * `resellers:access`, the reseller wall's page string.
 */

export const CRM_SEARCH_TYPES = [
  "customer",
  "prospect",
  "contact",
  "invoice",
  "quote",
  "file",
  "reseller",
] as const;

export type CrmSearchType = (typeof CRM_SEARCH_TYPES)[number];

/** The CRM SPA page-access string each result type needs. */
export const CRM_SEARCH_TYPE_PAGE: Readonly<Record<CrmSearchType, string>> = {
  customer: "customers:access",
  prospect: "prospects:access",
  contact: "contacts:access",
  invoice: "invoices:access",
  quote: "invoices:access",
  file: "file_manager:access",
  reseller: "resellers:access",
};

/**
 * The result types a viewer holding `pages` may see, in CRM_SEARCH_TYPES order.
 * `admin:access` is NOT a wildcard here: the SPA treats it as "every page", but
 * the dashboard emits it only for super_admin, alongside every page string, so
 * honouring it would only add a second way in.
 */
export function searchTypesForPages(pages: Iterable<string>): CrmSearchType[] {
  const held = new Set(pages);
  return CRM_SEARCH_TYPES.filter((type) => held.has(CRM_SEARCH_TYPE_PAGE[type]));
}
